import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody } from '@heroui/card';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { useAuth } from '../../contexts/AuthContext';

const ConfirmEmail: React.FC = () => {
  const pageTitle = 'Confirm Email';
  const pageDescription =
    'Confirm your email address to activate your account.';
  const { confirmEmail, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token') || '';
    const email = searchParams.get('email') || user?.email || undefined;

    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid email confirmation link.');
        return;
      }
      try {
        await confirmEmail(token, email);
        setStatus('success');
        setMessage('Your email has been confirmed successfully.');
      } catch (e) {
        setStatus('error');
        setMessage('Email confirmation failed or link has expired.');
      }
    };
    run();
  }, [confirmEmail, searchParams, user?.email]);

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <Card>
            <CardBody className='text-center py-10'>
              {status === 'loading' && (
                <>
                  <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4' />
                  <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                    Confirming your email...
                  </h2>
                  <p className='text-gray-600'>Please wait.</p>
                </>
              )}

              {status === 'success' && (
                <>
                  <CheckCircleIcon className='w-16 h-16 text-success-500 mx-auto mb-4' />
                  <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                    Email confirmed
                  </h2>
                  <p className='text-gray-600 mb-6'>{message}</p>
                  <Button
                    color='primary'
                    onClick={() => navigate('/account', { replace: true })}
                  >
                    Go to my account
                  </Button>
                </>
              )}

              {status === 'error' && (
                <>
                  <ExclamationTriangleIcon className='w-16 h-16 text-danger-500 mx-auto mb-4' />
                  <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                    Email confirmation failed
                  </h2>
                  <p className='text-danger-600 mb-6'>{message}</p>
                  <Button
                    variant='bordered'
                    onClick={() => navigate('/', { replace: true })}
                  >
                    Back to home
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ConfirmEmail;
